
import { inject } from "@angular/core";
import { HttpRequest, HttpHandlerFn, HttpEvent, HttpInterceptorFn, HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { SigninService } from '@shared/service/http_services/signin.service';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, take, filter, switchMap, retry, tap, takeUntil } from 'rxjs/operators';
import { ServiceResult } from "@shared/helper/service-result";
import { Router } from "@angular/router";
import { htmlUrls } from "@shared/utils/router-paths";
import { UserStateService } from "@app/state/user-state.service";

const isRefreshing = new BehaviorSubject<boolean>(false);
const refreshTokenSubject = new BehaviorSubject<any>(null);
const cancelRequests$ = new Subject<void>();

export const handleErrorInterceptor: HttpInterceptorFn = (request: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {
    const signinService = inject(SigninService);
    const router = inject(Router);
    const userState = inject(UserStateService);

    if (signinService.getUserAccessToken()) {
        request = addToken(request, signinService.getUserAccessToken()!);
    }
    return next(request).pipe(takeUntil(cancelRequests$), catchError(error => handleErrorFn(error, request, next, signinService, router, userState)));
};


const handleErrorFn = (errorResponse: any, request: any, next: any, signinService: SigninService, router: Router, userState: UserStateService): Observable<any> => {
    if (errorResponse instanceof HttpErrorResponse) {

        if (errorResponse.status == 401) {
            return handle401Error(request, next, signinService, router, userState);
        }
        else if (errorResponse.status == 429) {
            return of(errorResponse).pipe(
                tap(() => console.log('Retrying...')),
                retry({ delay: 4000, count: 5, resetOnSuccess: true }),
                catchError(err => of(new HttpResponse({
                    body: new ServiceResult(err?.error?.error, errorResponse?.error, true, err?.error?.error)
                })))
            );
        }
        else {
            if (!signinService.Repository.IsInBrowser) {
                console.error(`Custom Error LOG => request failed with url:${request.url}/n`, `status:${errorResponse.status}/n`);
            }
            return of(new HttpResponse({
                body: new ServiceResult(errorResponse?.error?.error, errorResponse?.error, true, errorResponse?.error?.error)
            }));
        }
    }
    return of(null);
};

const handle401Error = (request: HttpRequest<any>, next: HttpHandlerFn, signinService: SigninService, router: Router, userState: UserStateService): Observable<any> => {

    const access = signinService.getUserAccessToken();
    const refresh = signinService.getUserRefreshToken();

    if (!access && !refresh) {
        return endSession(signinService, router, userState);
    }

    if (!isRefreshing.getValue()) {
        // ? only first 401 req comes here and refresh process starts
        isRefreshing.next(true);
        refreshTokenSubject.next(null);
        return signinService.refreshToken().pipe(
            switchMap((token: any) => {
                isRefreshing.next(false);
                refreshTokenSubject.next(token?.access_token);
                return next(addToken(request, token?.access_token));
            }),
            catchError(() => {
                isRefreshing.next(false);
                return endSession(signinService, router, userState);
            })
        );
    } else {
        // ? reqs are queued while refreshing is pending
        return refreshTokenSubject.pipe(
            filter(token => token != null),
            take(1),
            switchMap(jwt => {
                return next(addToken(request, jwt));
            })
        );
    }

};

const addToken = (request: HttpRequest<any>, token: string): HttpRequest<any> => {
    return request.clone({
        setHeaders: {
            'Authorization': `Bearer ${token}`
        }
    });
};

const endSession = (signinService: SigninService, router: Router, userState: UserStateService) => {
    cancelRequests$.next();  //? cancel all queued pending requests
    signinService.repo.clearUserData();
    userState.userData = null;
    router.navigate([htmlUrls.home]);
    return of(new HttpResponse({ body: new ServiceResult("Session expired", null, true, "401") }));
}